import { Alert, Button, Divider, PasswordInput, Space, Stack, Text, TextInput } from '@mantine/core'
import { useForm, UseFormReturnType } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { showNotification } from '@mantine/notifications'
import { IconAlertCircle, IconCircleCheck, IconX } from '@tabler/icons-react'
import { Heading } from 'components'
import { EmailAlreadyRegisteredError } from 'helpers'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useStudentSignupContext } from '../context/StudentSignupContext'
import { createStudent, googleSignupStudent } from '../service/studentSignup'
import { Form, NameFieldsRow } from '../styles'
import { GoogleSignupButton } from './GoogleSignupButton'

interface FormValues {
  email: string
  password: string
  firstName: string
  lastName: string
  inviteCode: string
}

export const SignupForm = () => {
  const { t } = useTranslation()
  const { data, setData, user, setUser, urlCode, isGoogleAuth } = useStudentSignupContext()

  const [step, setStep] = useState(1)
  const [visible, { toggle }] = useDisclosure(false)
  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const usernameManuallyEditedRef = useRef(false)

  const buildUsernameFromName = (firstName: string, lastName: string) => {
    const sanitize = (value: string) =>
      value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
    const first = sanitize(firstName)
    const last = sanitize(lastName)
    if (!first && !last) return ''
    return [first, last].filter(Boolean).join('.')
  }

  const form = useForm<FormValues>({
    initialValues: {
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      inviteCode: data.inviteCode,
    },
    validateInputOnBlur: true,
    validate: step === 1
      ? {
          email: value => {
            if (!value) return t('student_signup.validation_username_required')
            if (value.length < 3 || value.length > 25) return t('student_signup.validation_username_length')
            if (!/^[a-zA-Z0-9_.]+$/.test(value)) return t('student_signup.validation_username_format')
            return null
          },
          password: value => {
            if (isGoogleAuth) return null
            if (!value) return t('student_signup.validation_password_required')
            if (value.length < 6) return t('student_signup.validation_password_min')
            return null
          },
          firstName: value => (!value ? t('student_signup.validation_first_name_required') : null),
          lastName: value => (!value ? t('student_signup.validation_last_name_required') : null),
        }
      : {
          inviteCode: value => (!value?.trim() ? t('student_signup.validation_invite_code_required') : null),
        },
  })

  useEffect(() => {
    if (data.email && data.email !== form.values.email) {
      form.setFieldValue('email', data.email)
    }
    if (data.firstName && data.firstName !== form.values.firstName) {
      form.setFieldValue('firstName', data.firstName)
    }
    if (data.lastName && data.lastName !== form.values.lastName) {
      form.setFieldValue('lastName', data.lastName)
    }
    if (data.inviteCode && data.inviteCode !== form.values.inviteCode) {
      form.setFieldValue('inviteCode', data.inviteCode)
    }
  }, [data.email, data.firstName, data.lastName, data.inviteCode])

  // Auto-fill username from first/last name unless the user already edited it
  // manually. Only writes when both names are present and the generated value
  // satisfies the username validation rules (length 3-25, alphanumeric + _ .),
  // so the user is never blocked at the "Next" step by an auto-filled value.
  useEffect(() => {
    if (isGoogleAuth) return
    if (usernameManuallyEditedRef.current) return
    const firstName = form.values.firstName.trim()
    const lastName = form.values.lastName.trim()
    if (!firstName || !lastName) return
    const generated = buildUsernameFromName(firstName, lastName)
    const isValidGenerated =
      generated.length >= 3 &&
      generated.length <= 25 &&
      /^[a-zA-Z0-9_.]+$/.test(generated)
    if (isValidGenerated && generated !== form.values.email) {
      form.setFieldValue('email', generated)
    }
  }, [form.values.firstName, form.values.lastName, isGoogleAuth])

  const handleStep1 = () => {
    const errors = form.validate()
    if (errors.hasErrors) return
    setData(form.values)
    setStep(2)
  }

  const handleSubmit = async (values: typeof form.values) => {
    setSubmitError(null)
    setLoading(true)
    setData(values)

    try {
      let response

      if (isGoogleAuth && data.googleToken) {
        response = await googleSignupStudent(
          {
            email: values.email,
            uid: data.googleToken,
            firstName: values.firstName,
            lastName: values.lastName,
          },
          undefined,
          values.inviteCode
        )
      } else {
        response = await createStudent({ ...values })
      }

      setUser(response.user)
      toast.success(t('student_signup.signup_success'))
    } catch (error: unknown) {
      if (error instanceof EmailAlreadyRegisteredError) {
        showNotification({
          title: 'Email Already Registered',
          message: 'The email address you entered is already associated with an existing account. Please log in or use a different email to sign up.',
          color: 'red',
          icon: <IconX size={18} />,
          autoClose: 8000,
        })
        return
      }
      const errorMessage = error instanceof Error ? error.message : t('student_signup.signup_error')
      setSubmitError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  if (user) {
    return (
      <Stack spacing='lg' align='center'>
        <IconCircleCheck size={64} color='teal' stroke={1.5} />
        <Heading size='xl' text={t('student_signup.success_screen_title')} />
        <Text align='center' color='dimmed'>
          {t('student_signup.success_screen_message')}
        </Text>
        <Button component={Link} to='/account/signin' size='lg' color='teal' radius='xl' fullWidth>
          {t('student_signup.go_to_login')}
        </Button>
      </Stack>
    )
  }

  return (
    <>
      <div style={{ textAlign: 'center' }}>
        <Heading size='xl' text={t('student_signup.page_title')} />
      </div>
      <Text color='dimmed' align='center' mb='lg'>
        {t('student_signup.page_subtitle')}
      </Text>

      {submitError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color='red'
          mb='md'
          withCloseButton
          onClose={() => setSubmitError(null)}
        >
          {submitError}
        </Alert>
      )}

      {step === 1 ? (
        <Step1Form
          form={form}
          isGoogleAuth={isGoogleAuth}
          visible={visible}
          toggle={toggle}
          onNext={handleStep1}
          onUsernameManualEdit={() => {
            usernameManuallyEditedRef.current = true
          }}
          t={t}
        />
      ) : (
        <Form onSubmit={form.onSubmit(handleSubmit)}>
          <TextInput
            size='md'
            label={t('student_signup.invite_code_label')}
            placeholder={t('student_signup.invite_code_placeholder')}
            readOnly={Boolean(urlCode)}
            disabled={Boolean(urlCode)}
            styles={urlCode ? { input: { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } } : undefined}
            {...form.getInputProps('inviteCode')}
          />

          <Divider my='lg' />

          <Button fullWidth size='xl' type='submit' loading={loading} color='teal' radius='xl'>
            {t('student_signup.sign_up_button')}
          </Button>

        </Form>
      )}
    </>
  )
}

function Step1Form({
  form,
  isGoogleAuth,
  visible,
  toggle,
  onNext,
  onUsernameManualEdit,
  t,
}: {
  form: UseFormReturnType<FormValues>
  isGoogleAuth: boolean
  visible: boolean
  toggle: () => void
  onNext: () => void
  onUsernameManualEdit: () => void
  t: (key: string) => string
}) {
  const usernameInputProps = form.getInputProps('email')

  return (
    <>
      {!isGoogleAuth && (
        <>
          <GoogleSignupButton />
          <Divider my='lg' label={t('student_signup.or_divider')} labelPosition='center' />
        </>
      )}

      <Form
        onSubmit={e => {
          e.preventDefault()
          onNext()
        }}
      >
        <NameFieldsRow>
          <TextInput
            size='md'
            style={{ flex: 1 }}
            label={t('student_signup.first_name_label')}
            placeholder={t('student_signup.first_name_placeholder')}
            {...form.getInputProps('firstName')}
          />
          <TextInput
            size='md'
            style={{ flex: 1 }}
            label={t('student_signup.last_name_label')}
            placeholder={t('student_signup.last_name_placeholder')}
            {...form.getInputProps('lastName')}
          />
        </NameFieldsRow>

        <Space h='md' />

        <TextInput
          size='md'
          label={t('student_signup.username_label')}
          placeholder={t('student_signup.username_placeholder')}
          readOnly={isGoogleAuth}
          disabled={isGoogleAuth}
          styles={isGoogleAuth ? { input: { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } } : undefined}
          {...usernameInputProps}
          onChange={event => {
            onUsernameManualEdit()
            usernameInputProps.onChange(event)
          }}
        />

        {!isGoogleAuth && (
          <PasswordInput
            mt='md'
            size='md'
            label={t('student_signup.password_label')}
            placeholder={t('student_signup.password_placeholder')}
            visible={visible}
            onVisibilityChange={toggle}
            {...form.getInputProps('password')}
          />
        )}

        <Divider my='lg' />

        <Button fullWidth size='xl' type='submit' color='teal' radius='xl'>
          {t('student_signup.sign_up_button')}
        </Button>

      </Form>
    </>
  )
}
